import type { VendorAssessmentControl } from "@/modules/agent/states/cusomize-controls";
import { getCustomizeControlsActions } from "@/modules/agent/states/cusomize-controls";
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";
import { useLoggedInMember } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles } from "@/modules/team/states/index.ts";
import { ZButton } from "@/shared/components/button";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { addNotification } from "@/shared/states/notification";
import { Input, Modal, ModalBody, ModalContent, ModalHeader } from "@nextui-org/react";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type ControlSet } from "../../requests";

interface BrowseControlSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  controlSet: ControlSet | null;
}

const BrowseControlSetModal = ({
  isOpen,
  onClose,
  controlSet,
}: BrowseControlSetModalProps) => {
  const navigate = useNavigate();
  const [controls, setControls] = useState<VendorAssessmentControl[]>([]);
  const [fetchingControls, setFetchingControls] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Get logged-in member for role-based access control
  const member = useLoggedInMember();
  const { isTeamOrgAdmin, isTeamAdmin } = member
    ? getZaniaTeamMemberRoles("", member)
    : { isTeamOrgAdmin: false, isTeamAdmin: false };

  // Check if user has edit permissions (admin only)
  const isAdmin = isTeamOrgAdmin || isTeamAdmin;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      if (controlSet?.controls_url) {
        fetchControlsFromBlob();
      } else {
        setControls([]);
      }
    }
  }, [isOpen, controlSet]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const fetchControlsFromBlob = async () => {
    if (!controlSet?.controls_url) return;
    
    setFetchingControls(true);
    try {
      // Use the same URL refresh logic as other parts of the app
      const validSignedUrl = await checkAndRefreshUrl(controlSet.controls_url);
      
      // Fetch controls data directly from blob storage
      const response = await fetch(validSignedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch controls: ${response.statusText}`);
      }
      
      const controlsData = await response.json();
      
      // Transform the data to match VendorAssessmentControl interface
      const transformedControls: VendorAssessmentControl[] = controlsData.map((control: any) => ({
        ...control,
        agentType: 'multi_file_vendor_assessment' as const,
        uid: control.uid || control.id || `control_${Math.random().toString(36).substr(2, 9)}`,
        category: control.category || control.key || '',
        question: control.question || '',
        section: control.section || 'zania_security_assessment' as const,
        edited_state: control.edited_state || 'default' as const,
        changesInQuestion: control.changesInQuestion || 0,
      }));
      
      setControls(transformedControls);
    } catch (error) {
      console.error('Error fetching controls:', error);
      addNotification({
        type: "error",
        title: "Failed to load controls",
        message: "Could not fetch control set data. Please try again.",
      });
    } finally {
      setFetchingControls(false);
    }
  };

  const handleClose = () => {
    setControls([]);
    setSearchQuery("");
    onClose();
  };

  const handleEditControls = () => {
    if (controlSet?.id && controlSet?.controls_url) {
      // Set control set edit context in store instead of URL params
      const { setControlSetEditContext } = getCustomizeControlsActions();
      setControlSetEditContext({
        isControlSetEdit: true,
        controlSetId: controlSet.id,
        controlsUrl: controlSet.controls_url,
        controlSetName: controlSet.controls_set_name,
      });
      
      // Close modal first
      handleClose();
      
      // Navigate after a small delay to ensure state is set and modal is closed
      setTimeout(() => {
        navigate('/settings/control-sets/edit');
      }, 100);
    }
  };

  // Filter controls based on search query
  const filteredControls = controls.filter(control => 
    control.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    control.question.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="5xl"
      hideCloseButton={true}
      scrollBehavior="inside"
      classNames={{
        base: "max-w-[96vw] h-[95vh] my-2",
        wrapper: "z-[9999] overflow-hidden",
      }}
    >
      <ModalContent className="max-h-[95vh] rounded-xl">
        {/* Full header for view mode - Matching Custom Controls Header */}
        <ModalHeader className="px-6 py-4 grid grid-cols-3 min-h-[69px] bg-white gap-2 border-b rounded-t-xl">
          {/* Left: Title and Count */}
          <div className="col-span-1 items-center my-auto">
            <div className="flex items-center">
              <button
                onClick={handleClose}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors mr-2"
              >
                <X size={16} className="text-gray-500" />
              </button>
              <div className="text-sm font-medium">{controlSet?.controls_set_name || "Control Set"}</div>
              {controlSet && (
                <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg ml-2">
                  <p className="text-[10px] text-[#52525B] leading-4 font-medium">{controls.length}</p>
                </div>
              )}
            </div>
          </div>

          {/* Center: Search Bar */}
          <div className="col-span-1">
            {controlSet && (
              <Input
                classNames={{
                  inputWrapper: "rounded-md h-10 min-h-9",
                  mainWrapper: "max-h-10",
                }}
                className="grow max-w-96 min-w-[480px] h-10 placeholder:text-zinc-400"
                placeholder="Search controls by keywords"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
            )}
          </div>

          {/* Right: Edit Button */}
          <div className="col-span-1 flex justify-end items-center my-auto">
            <div className="flex flex-row gap-1">
              {controlSet && isAdmin && (
                <ZButton
                  color="primary"
                  variant="bordered"
                  className="text-xs font-medium max-h-7 max-w-32 border-1"
                  onClick={handleEditControls}
                >
                  Edit Controls
                </ZButton>
              )}
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="flex-1 p-0 overflow-hidden rounded-b-xl">
          {/* View Mode Content - when controlSet is provided (row click) */}
          <div className="flex flex-col h-full">
            {fetchingControls ? (
              <div className="flex items-center justify-center flex-1">
                <div className="flex flex-col items-center gap-3">
                  <LoadingSpinner />
                  <p className="text-sm text-gray-500">Loading controls...</p>
                </div>
              </div>
            ) : controls.length === 0 ? (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center">
                  <p className="text-gray-500">No controls found in this control set.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Table Headers - Matching Custom Controls */}
                <div className="flex items-center px-1 py-3 bg-[#F9F7F7] border-b border-[#E4E4E7]">
                  <div className="w-[240px] max-w-[240px] px-1 pl-4">
                    <div className="text-[12px] leading-5 font-semibold">Category</div>
                  </div>
                  <div className="flex-1 px-1 pl-4">
                    <div className="text-[12px] leading-5 font-semibold">Control Description</div>
                  </div>
                </div>

                {/* Table Body - Matching Custom Controls */}
                <div className="flex-1 overflow-y-auto bg-white">
                  {filteredControls.map((control) => (
                    <div
                      key={control.uid}
                      className="flex items-start px-1 py-3 border-t-1 border-b-1 border-[#E4E4E7] bg-white hover:bg-[#ECECEC] min-h-[48px] group"
                    >
                      <div className="w-[240px] max-w-[240px] px-1 align-baseline pl-4">
                        <div className="pt-1">
                          <div className="text-sm font-normal text-zinc-700 p-px border-none capitalize p-[4px] break-words">
                            {control.category.replace(/_/g, " ")}
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 px-1 align-baseline pl-4">
                        <div className="pt-1">
                          <div className="text-sm font-normal text-zinc-700 p-px border-none p-[4px] leading-relaxed break-words">
                            {control.question}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BrowseControlSetModal;